"use strict";
/*
 * 8x8 Confidential & Proprietary || Copyright 2025 8x8, Inc. or its affiliates. All Rights Reserved.
 *  _____      _____
 * |  _  |    |  _  |
 *  \ V /__  __\ V /   ___ ___  _ __ ___
 *  / _ \\ \/ // _ \  / __/ _ \| '_ ` _ \
 * | |_| |>  <| |_| || (_| (_) | | | | | |
 * \_____/_/\_\_____(_)___\___/|_| |_| |_|
 *
 * @License https://www.8x8.com/terms-and-conditions/software-development-kit-license-agreement
 * @Author Comanici Paul
 * @Date 2025/08/28
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.cancelReceive = exports.getActiveTransfers = exports.setReceiveOptions = void 0;
exports.cleanupFileReceiver = cleanupFileReceiver;
exports.createFileReceiveFunction = createFileReceiveFunction;
exports.getFileReceiver = getFileReceiver;
const tslib_1 = require("tslib");
const dependencies_1 = require("./dependencies");
const observability_codes_1 = require("./observability.codes");
const constants_1 = require("./constants");
const completed_1 = require("./completed");
const crypto_1 = require("./crypto");
// Global state for all instances
const activeTransfers = new Map();
const pendingChunkOperations = new Map();
const fileReceiverInstances = new Map();
const reconstructFile = (transfer, instanceId) => {
    const sortedChunks = [];
    const { totalChunks, receivedChunks, fileName, fileId } = transfer;
    for (let i = 0; i < totalChunks; i++) {
        const chunk = receivedChunks.get(i);
        if (chunk) {
            sortedChunks.push(chunk);
        }
        else {
            const error = new Error(`Missing chunk ${i} for file ${fileName}`);
            (0, dependencies_1.pushError)(instanceId, error, {
                context: {
                    code: observability_codes_1.FILE_CODES.FILE_RECONSTRUCTION_ERROR,
                    fileId,
                    fileName,
                    missingChunk: i,
                },
            });
            throw error;
        }
    }
    return new Blob(sortedChunks);
};
const handleSendFile = (value, instanceId) => {
    const { fileId, metadata } = value;
    const fileMetadata = metadata;
    const fileName = String((fileMetadata === null || fileMetadata === void 0 ? void 0 : fileMetadata.name) || "unknown-file");
    const encrypted = Boolean(fileMetadata === null || fileMetadata === void 0 ? void 0 : fileMetadata.encrypted);
    const encryptionKey = fileMetadata === null || fileMetadata === void 0 ? void 0 : fileMetadata.encryptionKey;
    const transfer = {
        fileId: String(fileId),
        fileName,
        totalChunks: 0, // Updated when first chunk arrives
        receivedChunks: new Map(),
        receivedBytes: 0, // Initialize incremental byte tracking for performance
        metadata: fileMetadata,
        encrypted,
        encryptionKey,
        startTime: Date.now(),
        options: fileReceiverInstances.get(instanceId) || {},
    };
    activeTransfers.set(String(fileId), transfer);
    (0, dependencies_1.pushLog)(instanceId, [`Starting file receive for ${fileName} (encrypted: ${encrypted})`], {
        context: {
            code: observability_codes_1.FILE_CODES.FILE_RECEIVE_START,
            fileId: String(fileId),
            fileName,
            encrypted,
        },
    });
};
const removePendingChunkOperation = (fileId, chunkIndex) => {
    var _a, _b;
    (_a = pendingChunkOperations.get(fileId)) === null || _a === void 0 ? void 0 : _a.delete(chunkIndex);
    const hasNoPendingOperations = ((_b = pendingChunkOperations.get(fileId)) === null || _b === void 0 ? void 0 : _b.size) === 0;
    if (hasNoPendingOperations) {
        pendingChunkOperations.delete(fileId);
    }
};
const handleChunk = (value, instanceId) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
    const { fileId: chunkFileId, chunkIndex, data: chunkData, totalChunks, iv } = value;
    const fileIdStr = String(chunkFileId);
    const chunkIndexNum = Number(chunkIndex);
    const transfer = activeTransfers.get(fileIdStr);
    if (!transfer) {
        (0, dependencies_1.pushError)(instanceId, new Error(`No active transfer found for file ID: ${chunkFileId}`), {
            context: { code: observability_codes_1.FILE_CODES.RECEIVER_TRANSFER_NOT_FOUND, fileId: String(chunkFileId) },
        });
        return;
    }
    // Track this chunk as pending
    const needsNewPendingSet = !pendingChunkOperations.has(fileIdStr);
    if (needsNewPendingSet) {
        pendingChunkOperations.set(fileIdStr, new Set());
    }
    const chunkSet = pendingChunkOperations.get(fileIdStr);
    if (chunkSet) {
        chunkSet.add(chunkIndexNum);
    }
    // Update totalChunks if not set yet
    const isFirstChunk = transfer.totalChunks === 0;
    if (isFirstChunk) {
        transfer.totalChunks = Number(totalChunks);
    }
    try {
        let chunkBuffer;
        const isEncrypted = transfer.encrypted && transfer.encryptionKey && iv;
        if (isEncrypted) {
            // Decrypt encrypted chunk
            (0, dependencies_1.pushLog)(instanceId, [`Decrypting chunk ${chunkIndexNum + 1}/${transfer.totalChunks} for ${transfer.fileName}`], {
                context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_CHUNK, fileId: fileIdStr, chunkIndex: chunkIndexNum },
            });
            if (!transfer.encryptionKey) {
                throw new Error("Encryption key is required for encrypted chunks");
            }
            // Convert base64 encryption key to ArrayBuffer for importEncryptionKey
            const keyData = (() => {
                const binaryString = atob(transfer.encryptionKey);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                return bytes.buffer;
            })();
            const key = yield (0, crypto_1.importEncryptionKey)(keyData, instanceId);
            const encryptedData = chunkData;
            const ivBuffer = iv;
            chunkBuffer = yield (0, crypto_1.decryptData)(encryptedData, key, ivBuffer, instanceId);
        }
        else {
            // Handle unencrypted chunk - data is already ArrayBuffer
            chunkBuffer = chunkData;
        }
        transfer.receivedChunks.set(chunkIndexNum, chunkBuffer);
        transfer.receivedBytes += chunkBuffer.byteLength; // Increment total bytes for performance
        (0, dependencies_1.pushLog)(instanceId, [`Processed chunk ${chunkIndexNum + 1}/${transfer.totalChunks} for ${transfer.fileName}`], {
            context: {
                code: observability_codes_1.FILE_CODES.FILE_RECEIVE_CHUNK,
                fileId: fileIdStr,
                chunkIndex: chunkIndexNum,
                totalChunks: transfer.totalChunks,
            },
        });
        // Update progress if callback provided
        updateProgress(fileIdStr);
    }
    catch (error) {
        (0, dependencies_1.pushError)(instanceId, error, {
            context: {
                code: observability_codes_1.FILE_CODES.RECEIVER_CHUNK_DECRYPT_ERROR,
                fileId: fileIdStr,
                chunkIndex: chunkIndexNum,
            },
        });
        removePendingChunkOperation(fileIdStr, chunkIndexNum);
        return;
    }
    removePendingChunkOperation(fileIdStr, chunkIndexNum);
    // Check if all chunks received
    const allChunksReceived = transfer.receivedChunks.size === transfer.totalChunks;
    if (allChunksReceived) {
        handleFileComplete(fileIdStr, instanceId);
    }
});
const handleComplete = (value, instanceId, onMessage) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
    const { fileId: completeFileId } = value;
    const fileKey = String(completeFileId);
    (0, dependencies_1.pushLog)(instanceId, [`File transfer completion signal received for ${fileKey}`], {
        context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_COMPLETE, fileId: fileKey },
    });
    const pendingSet = pendingChunkOperations.get(fileKey);
    const hasPendingOperations = pendingSet && pendingSet.size > 0;
    if (hasPendingOperations) {
        // Wait for pending operations with timeout
        const maxWaitTime = 5000; // 5 seconds
        const pollInterval = 100; // 100ms
        let waitTime = 0;
        while (waitTime < maxWaitTime) {
            const currentPendingSet = pendingChunkOperations.get(fileKey);
            if (!currentPendingSet || currentPendingSet.size === 0) {
                break;
            }
            yield new Promise((resolve) => setTimeout(resolve, pollInterval));
            waitTime += pollInterval;
        }
        const timedOut = waitTime >= maxWaitTime;
        if (timedOut) {
            (0, dependencies_1.pushError)(instanceId, new Error(`Timeout waiting for pending operations for file ID: ${completeFileId}`), {
                context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR, fileId: fileKey },
            });
        }
    }
    // Send completion message back to partner with the completed file
    const completedFile = (0, completed_1.getCompletedFile)(fileKey);
    if (completedFile && onMessage) {
        (0, dependencies_1.pushLog)(instanceId, [`Sending file transfer completion message for ${completedFile.fileName}`], {
            context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_COMPLETE, fileId: fileKey },
        });
        onMessage({
            type: dependencies_1.OWN_DATA_TYPES.FILE_TRANSFER_COMPLETE,
            message: Object.assign(Object.assign({}, completedFile), { fileKey }),
            schemaVersion: dependencies_1.IFRAME_MESSAGE_SCHEMA_VERSION,
        });
    }
    else if (!completedFile) {
        (0, dependencies_1.pushLog)(instanceId, [`No completed file found for key: ${fileKey}`], {
            context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR, fileId: fileKey },
        });
    }
});
const handleCancel = (value, instanceId) => {
    const { fileId: cancelFileId } = value;
    const fileIdStr = String(cancelFileId);
    cleanup(fileIdStr);
    (0, dependencies_1.pushLog)(instanceId, [`File transfer cancelled for ${fileIdStr}`], {
        context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR, fileId: fileIdStr },
    });
};
const handleError = (value, instanceId) => {
    const { fileId: errorFileId, error } = value;
    const fileIdStr = String(errorFileId);
    cleanup(fileIdStr);
    (0, dependencies_1.pushError)(instanceId, new Error(`File transfer error: ${error}`), {
        context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR, fileId: fileIdStr },
    });
};
const handleFileComplete = (fileId, instanceId) => {
    const transfer = activeTransfers.get(fileId);
    if (!transfer) {
        return;
    }
    try {
        const reconstructedFile = reconstructFile(transfer, instanceId);
        const duration = Date.now() - transfer.startTime;
        (0, completed_1.setCompletedFile)(fileId, {
            fileName: transfer.fileName,
            blob: reconstructedFile,
            metadata: transfer.metadata,
        });
        // Notify completion if callback provided
        const completeCallback = transfer.options.onComplete;
        if (completeCallback) {
            completeCallback({
                fileId,
                fileName: transfer.fileName,
                success: true,
                transferredBytes: reconstructedFile.size,
                totalBytes: reconstructedFile.size,
                duration,
                fileKey: fileId,
                blob: reconstructedFile,
            });
        }
        (0, dependencies_1.pushLog)(instanceId, [`File ${transfer.fileName} reconstructed successfully`], {
            context: {
                code: observability_codes_1.FILE_CODES.FILE_RECONSTRUCTION_SUCCESS,
                fileId,
                fileName: transfer.fileName,
                duration,
                fileSize: reconstructedFile.size,
            },
        });
        cleanup(fileId);
    }
    catch (error) {
        (0, dependencies_1.pushError)(instanceId, error, {
            context: {
                code: observability_codes_1.FILE_CODES.FILE_RECONSTRUCTION_ERROR,
                fileId,
                fileName: transfer.fileName,
            },
        });
    }
};
const updateProgress = (fileId) => {
    var _a;
    const transfer = activeTransfers.get(fileId);
    const hasProgressCallback = transfer && transfer.options.onProgress;
    if (!hasProgressCallback) {
        return;
    }
    const progress = (transfer.receivedChunks.size / transfer.totalChunks) * 100;
    const receivedBytes = transfer.receivedBytes; // Use incremental tracking instead of O(n²) calculation
    const progressCallback = transfer.options.onProgress;
    if (progressCallback) {
        progressCallback({
            fileId,
            fileName: transfer.fileName,
            progress,
            transferredChunks: transfer.receivedChunks.size,
            totalChunks: transfer.totalChunks,
            transferredBytes: receivedBytes,
            totalBytes: ((_a = transfer.metadata) === null || _a === void 0 ? void 0 : _a.size) || 0,
            // Include receiver-specific aliases
            receivedChunks: transfer.receivedChunks.size,
            receivedBytes,
        });
    }
};
const cleanup = (fileId) => {
    activeTransfers.delete(fileId);
    pendingChunkOperations.delete(fileId);
};
const handleFileTransferMessage = (data, instanceId, onMessage) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
    const { action, value } = data.message;
    try {
        switch (action) {
            case constants_1.FILE_TRANSFER_ACTIONS.SEND_FILE:
                handleSendFile(value, instanceId);
                break;
            case constants_1.FILE_TRANSFER_ACTIONS.CHUNK:
                yield handleChunk(value, instanceId);
                break;
            case constants_1.FILE_TRANSFER_ACTIONS.COMPLETE:
                yield handleComplete(value, instanceId, onMessage);
                break;
            case constants_1.FILE_TRANSFER_ACTIONS.CANCEL:
                handleCancel(value, instanceId);
                break;
            case constants_1.FILE_TRANSFER_ACTIONS.ERROR:
                handleError(value, instanceId);
                break;
            default:
                (0, dependencies_1.pushLog)(instanceId, [`Unknown file transfer action: ${action}`], {
                    context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR },
                });
        }
    }
    catch (error) {
        (0, dependencies_1.pushError)(instanceId, error, {
            context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR, action },
        });
    }
});
const setReceiveOptions = (fileId, options, instanceId) => {
    const transfer = activeTransfers.get(fileId);
    if (transfer) {
        transfer.options = Object.assign(Object.assign({}, transfer.options), options);
    }
    // Also update instance-level options
    fileReceiverInstances.set(instanceId, Object.assign(Object.assign({}, fileReceiverInstances.get(instanceId)), options));
};
exports.setReceiveOptions = setReceiveOptions;
const getActiveTransfers = () => {
    return Array.from(activeTransfers.keys());
};
exports.getActiveTransfers = getActiveTransfers;
const cancelReceive = (fileId, instanceId) => {
    cleanup(fileId);
    (0, dependencies_1.pushLog)(instanceId, [`File receive cancelled for ${fileId}`], {
        context: { code: observability_codes_1.FILE_CODES.FILE_RECEIVE_ERROR, fileId },
    });
};
exports.cancelReceive = cancelReceive;
// File receiver instances for compatibility
const fileReceiverAPIs = new Map();
// Factory function to get/create FileReceiver API for an instanceId
function getFileReceiver(instanceId) {
    const hasExistingAPI = fileReceiverAPIs.has(instanceId);
    if (!hasExistingAPI) {
        // Initialize instance-level options
        fileReceiverInstances.set(instanceId, {});
        const api = {
            handleFileTransferMessage: (data, onMessage) => handleFileTransferMessage(data, instanceId, onMessage),
            setReceiveOptions: (fileId, options) => setReceiveOptions(fileId, options, instanceId),
            getActiveTransfers: () => getActiveTransfers(),
            cancelReceive: (fileId) => cancelReceive(fileId, instanceId),
        };
        fileReceiverAPIs.set(instanceId, api);
    }
    const api = fileReceiverAPIs.get(instanceId);
    if (!api) {
        throw new Error(`FileReceiver API not found for instanceId: ${instanceId}`);
    }
    return api;
}
// Factory function to create file receive handler for an instance
function createFileReceiveFunction(instanceId, options = {}) {
    // Store instance-level options
    fileReceiverInstances.set(instanceId, options);
    return (data, onMessage) => {
        return handleFileTransferMessage(data, instanceId, onMessage);
    };
}
function cleanupFileReceiver(instanceId) {
    // Clean up all transfers for this instance
    const transfersToDelete = Array.from(activeTransfers.entries())
        .filter(([, transfer]) => transfer.fileId.startsWith(instanceId))
        .map(([fileId]) => fileId);
    transfersToDelete.forEach((fileId) => cleanup(fileId));
    fileReceiverInstances.delete(instanceId);
    fileReceiverAPIs.delete(instanceId);
}
//# sourceMappingURL=receiver.js.map